import { AssessmentInfo, FileUploadOption } from "@verity/api-client/types";
import { VERITY_ATTRIBUTES } from "@verity/intercept-strategy";
import i18n from "../../../i18n";
import apiClient from "../../../helpers/api-client";

export const baseTransKey = "portal.components.takeAssessment";

const ERROR_PAGE_KEYS = [
	"noAssessmentFound",
	"isDeleted",
	"notPublished",
	"beforeStart",
	"afterEnd",
	"attemptsExceeded",
] as const;

export type ErrorPageKey = (typeof ERROR_PAGE_KEYS)[number];

/**Error UI uses the welcome page*/
export type ErrorWelcomePageKey = Extract<
	ErrorPageKey,
	"noAssessmentFound" | "isDeleted" | "notPublished" | "beforeStart"
>;

export function isErrorWelcomePageKey(key: string | null): key is ErrorWelcomePageKey {
	return key !== null && ["noAssessmentFound", "isDeleted", "notPublished", "beforeStart"].includes(key);
}

export function getErrorPageMessage(errorPageKey: ErrorPageKey | null): string | undefined {
	if (!errorPageKey) return undefined;
	return i18n.t(`${baseTransKey}.error.${errorPageKey}`) as string;
}

export function getStartAssessmentDisabledMessage(
	errorPageKey: ErrorPageKey | null,
	assessmentInfo: AssessmentInfo | undefined,
	hasListenersLoaded: boolean,
	isStartingProctoring: boolean,
): string | undefined {
	if (errorPageKey || !assessmentInfo) {
		return getErrorPageMessage(errorPageKey ?? "noAssessmentFound");
	}

	if (!hasListenersLoaded || isStartingProctoring) {
		return i18n.t(`${baseTransKey}.error.loading`) as string;
	}
}

export function getAcceptedFileFormatDescription(acceptedFileExt?: string[]): string {
	const isAny = !acceptedFileExt || acceptedFileExt.length === 0;

	const uploadInstructions = isAny
		? (i18n.t(`${baseTransKey}.acceptedFormatsAny`) as string)
		: (i18n.t(`${baseTransKey}.acceptedFormats`, {
				formats: acceptedFileExt.map((f) => f.toUpperCase()).join(", "),
			}) as string);
	return uploadInstructions;
}

type GetQuizSessionDataResponse = Awaited<ReturnType<typeof apiClient.proctoringSession.getStudentQuizSessionData>>;

function getVerityAssignmentInfo(res: GetQuizSessionDataResponse) {
	return res.questions.find((q) => q.questionType === "FileUpload");
}

export function extractVerityAssignmentInstructions(res: GetQuizSessionDataResponse): string {
	const fileUploadQuestion = getVerityAssignmentInfo(res);
	return fileUploadQuestion?.questionText ?? "";
}

export function extractFileFormats(res: GetQuizSessionDataResponse): string[] {
	const fileUploadQuestion = getVerityAssignmentInfo(res);

	if (!fileUploadQuestion) {
		return [];
	}

	const fileOptions = fileUploadQuestion.options as FileUploadOption | undefined;
	return fileOptions?.allowedFileTypes ?? [];
}

// Copied from theme
const CSC_ORIGIN = document.location.ancestorOrigins?.[0] ?? document.referrer.replace(/\/$/, "") ?? "";

function sendMessageToExtension(message: unknown) {
	if (!CSC_ORIGIN) {
		console.error("CSC_ORIGIN is not defined. Cannot send message to extension.");
		return;
	}
	// Check if we are in an iframe
	const target = window.parent !== window ? window.parent : window;
	target.postMessage(message, CSC_ORIGIN);
}

export function askIfListenersLoaded() {
	const message = { type: VERITY_ATTRIBUTES.MESSAGE.TYPE.VERITY_ASSESSMENT_LISTENERS_ASK_IF_LOADED };
	sendMessageToExtension(message);
}

export function startPreAssessmentCheck(assessmentInfo: AssessmentInfo) {
	const message = { type: VERITY_ATTRIBUTES.MESSAGE.TYPE.START_PRE_CHECK, assessmentInfo };
	sendMessageToExtension(message);
}

export function sendGetProctoringSessionId() {
	const message = { type: VERITY_ATTRIBUTES.MESSAGE.TYPE.GET_PROCTORING_SESSION_ID };
	sendMessageToExtension(message);
}

export function stopProctoring() {
	const message = { type: VERITY_ATTRIBUTES.MESSAGE.TYPE.STOP_PROCTORING };
	sendMessageToExtension(message);
}

type handlePreAssessmentCheckResponseProps = {
	event: MessageEvent;
	onLoaded?: (isExempted?: boolean) => void;
	onSuccess?: (proctoringSessionId: string) => void;
	onError?: (errorType?: string, errorDetails?: string) => void;
	onProctoringSessionIdResponse?: (proctoringSessionId: string | undefined) => void;
	onStopProctoringResponse?: () => void;
};

export function handleExtensionBridge(props: handlePreAssessmentCheckResponseProps) {
	const { event, onLoaded, onSuccess, onError, onProctoringSessionIdResponse, onStopProctoringResponse } = props;

	if (event.origin !== CSC_ORIGIN) {
		return;
	}

	if (event.source !== window && event.source !== window.parent) {
		return;
	}

	const request = event.data as {
		type: string;
		proctoringSessionId?: string;
		errorType?: string;
		errorDetails?: string;
		isExempted?: boolean;
	};
	if (request.type === VERITY_ATTRIBUTES.MESSAGE.TYPE.VERITY_ASSESSMENT_LISTENERS_LOADED) {
		onLoaded?.(request.isExempted);
	} else if (request.type === VERITY_ATTRIBUTES.MESSAGE.TYPE.GET_PROCTORING_SESSION_ID_RESPONSE) {
		onProctoringSessionIdResponse?.(request.proctoringSessionId ?? undefined);
	} else if (request.type === VERITY_ATTRIBUTES.MESSAGE.TYPE.PRE_CHECK_FINISHED) {
		if (request.proctoringSessionId) {
			onSuccess?.(request.proctoringSessionId);
		} else {
			console.warn("Pre-assessment check finished without proctoring session id");
			onError?.("error", "Pre-assessment check finished without proctoring session id");
		}
	} else if (request.type === VERITY_ATTRIBUTES.MESSAGE.TYPE.VERITY_ASSESSMENT_ERROR) {
		onError?.(request.errorType, request.errorDetails);
	} else if (request.type === VERITY_ATTRIBUTES.MESSAGE.TYPE.STOP_PROCTORING_RESPONSE) {
		onStopProctoringResponse?.();
	}
}
